/**
 * The per-record history contract (docs/01 §7; docs/11 F-1).
 *
 * ONE query shape for every record's History tab, because Matt's rule is that
 * the store is written once and each page derives from it: nine pages with nine
 * filter contracts would be nine chances for "filter by who" to mean something
 * slightly different.
 */

import { z } from 'zod';

import { AUDIT_CATEGORIES } from '../audit-catalog.ts';

export const historyQuerySchema = z.object({
  /** A grouping. Coarse on purpose: seven buckets a reader can scan. */
  category: z.enum(AUDIT_CATEGORIES).optional(),
  /**
   * ONE action, for when the category is too coarse to help.
   *
   * Matt, 2026-08-25: "the history filters across the entire app have nothing to
   * sort by." Category alone was the problem — about 70 of the 99 actions live
   * in `record`, so picking it filtered almost nothing. The picker offers only
   * the actions this record's history actually contains (`presentActions`), so
   * this is a short list of real choices rather than a 99-item enum.
   *
   * Not an enum here: the ledger outlives the catalog, and a history that
   * refuses to filter on an action it is currently displaying would be worse
   * than one that filters on a string the server simply will not match.
   */
  action: z.string().trim().min(1).max(100).optional(),
  actorUserId: z.uuid().optional(),
  /** Inclusive calendar dates. `to` covers the whole day it names. */
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
  /**
   * WHICH RECORD in the feed (Matt, 2026-08-26: *"History Filters at each page …
   * we need to include more filter options"*).
   *
   * A parent's history is its own plus its children's — a vendor's includes its
   * agreements, accounts and contacts — and `action` cannot separate them:
   * `note.created` on an agreement and on the vendor are the same action. Not an
   * enum, for the same reason `action` is not: the ledger outlives the code, and
   * a feed that refuses to filter on a kind it is displaying is worse than one
   * that filters on a string the server will not match.
   */
  entityType: z.string().trim().min(1).max(60).optional(),
  /**
   * ONE FIELD, across every action that touched it. "When did the end date last
   * move" is the question a lease or an agreement gets asked, and neither the
   * category nor the action narrows to it — every one of them writes a diff with
   * ten keys in it. The picker offers only the fields the feed actually recorded.
   */
  field: z.string().trim().min(1).max(80).optional(),
});
export type HistoryQuery = z.infer<typeof historyQuerySchema>;

export interface HistoryRow {
  readonly id: string;
  readonly occurredAt: string;
  readonly actorUserId: string;
  readonly actorName: string | null;
  readonly onBehalfOfName: string | null;
  readonly entityType: string;
  readonly action: string;
  readonly fieldDiffs: Record<string, { from: unknown; to: unknown }> | null;
  /**
   * The bulk run this entry belongs to, by KIND, or null for the ordinary
   * one-record change (0071, ADR-67). Turned into "Bulk Import Process by Rob
   * Nunez" by `bulkTag()`, composed with `actorName` on this same row — so the
   * tag names the ledger's own actor and cannot name anybody else.
   */
  readonly batchKind: string | null;
}

export interface HistoryPage {
  readonly rows: readonly HistoryRow[];
  /** The cap hid older events. The screen has to say so, not imply completeness. */
  readonly hasMore: boolean;
  /** Everyone in this record's UNFILTERED history — the actor picker's options. */
  readonly actors: readonly { id: string; name: string | null }[];
  /**
   * Every action this record's UNFILTERED history contains — the "what changed"
   * picker's options. The client groups them by category for display; it never
   * offers a category or an action the record has never produced.
   */
  readonly presentActions: readonly string[];
  /**
   * Every RECORD KIND this feed contains. One entry means the feed is about one
   * record, so the screen hides the control: a select with a single option is a
   * worse answer than no select.
   */
  readonly presentEntityTypes: readonly string[];
  /** Every field any event in this feed recorded a change to. */
  readonly presentFields: readonly string[];
}
